/**
 * XBRL Export Service
 * Handles communication with AWS Lambda function for XBRL generation
 */

const LAMBDA_URL = 'https://lrdfuuakqq63fo2waaoipro7yy0ckvmg.lambda-url.ap-southeast-1.on.aws/';
/** Replace with your Singapore Lambda URL when the export-xbrl-singapore Lambda is deployed */
const LAMBDA_URL_SG = 'https://xze7vii2e6sql5tuky4l2sin6e0fszne.lambda-url.ap-southeast-1.on.aws/';

export type XbrlCountry = 'MY' | 'SG';

export interface XBRLGenerationError {
  error: string;
  details?: string;
}

/**
 * Generate and download XBRL file for a filing
 * @param filingId - The UUID of the filing
 * @param country - 'MY' for Malaysia (single .xml zip), 'SG' for Singapore (modular .xbrl/.xsd/linkbase zip). Default 'MY'
 * @returns Promise that resolves when download starts
 */
export const downloadXBRL = async (
  filingId: string,
  country: XbrlCountry = 'MY'
): Promise<void> => {
  const baseUrl = country === 'SG' ? LAMBDA_URL_SG : LAMBDA_URL;
  try {
    console.log(`📥 Requesting XBRL generation for filing: ${filingId} (${country})`);
    console.log(`📡 Lambda URL: ${baseUrl}`);

    const response = await fetch(baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filing_id: filingId
      })
    });

    console.log(`📡 Response status: ${response.status}`);
    console.log(`📡 Response headers:`, response.headers);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Error response:', errorText);
      
      let error: XBRLGenerationError;
      try {
        error = JSON.parse(errorText);
      } catch {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
      
      throw new Error(error.details || error.error || 'XBRL generation failed');
    }

    console.log('✅ XBRL generated successfully, downloading...');

    // Get the blob from response
    const blob = await response.blob();
    
    console.log(`📦 Downloaded blob size: ${blob.size} bytes, type: ${blob.type}`);
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `${filingId}_xbrl.zip`; // Fallback
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    console.log(`📄 Filename from header: ${filename}`);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;  // Use extracted filename
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ XBRL file downloaded successfully');
  } catch (error) {
    console.error('❌ XBRL generation error:', error);
    throw error;
  }
};

/**
 * Check Lambda function health
 * @returns Promise<boolean> - true if Lambda is responsive
 */
export const checkXBRLServiceHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(LAMBDA_URL, {
      method: 'OPTIONS'
    });
    return response.ok;
  } catch (error) {
    console.error('XBRL service health check failed:', error);
    return false;
  }
};